// Estimates (optional feature) — legacy desktop Customers ▸ Create Estimates + progress invoicing.
// List with status; turn an estimate into an invoice (all of it, a %, or the rest), PDF, accept/reject/close.
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { money, fmtDate } from '../money';
import { FileText, Plus } from 'lucide-react';

const CARD = 'bg-white text-slate-800 border-slate-200';
const errOf = (e) => e?.response?.data?.detail || 'Request failed';
const today = () => new Date().toISOString().slice(0, 10);
const STATUS = { pending: 'Pending', accepted: 'Accepted', rejected: 'Rejected', closed: 'Closed' };

export function EstimatesPage({ base, go }) {
  const [rows, setRows] = useState(null); const [filter, setFilter] = useState('open');
  const [msg, setMsg] = useState(''); const [err, setErr] = useState('');
  const load = useCallback(() => axios.get(`${base}/estimates`, { params: filter === 'all' ? {} : { status: filter } })
    .then((r) => setRows(r.data || [])).catch((e) => { setErr(errOf(e)); setRows([]); }), [base, filter]);
  useEffect(() => { load(); }, [load]);
  const act = async (fn, ok) => { setErr(''); setMsg(''); try { await fn(); setMsg(ok); load(); } catch (e) { setErr(errOf(e)); } };
  const invoice = (e, mode) => {
    let percent = null;
    if (mode === 'percent') {
      // eslint-disable-next-line no-alert
      percent = window.prompt(`Invoice what % of estimate ${e.num}? (${money(e.remaining)} left)`, '50');
      if (!percent) return;
    }
    act(() => axios.post(`${base}/estimates/${e.id}/invoice`, { date: today(), mode, percent: percent ? Number(percent) : null }),
      `Invoice created from estimate ${e.num}`);
  };
  const pdf = async (e) => {
    const r = await axios.get(`${base}/estimates/${e.id}/pdf`, { responseType: 'blob' });
    window.open(URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' })), '_blank');
  };
  const status = (e, s) => act(() => axios.put(`${base}/estimates/${e.id}`, { status: s }), `Estimate ${e.num} ${STATUS[s].toLowerCase()}`);
  const del = (e) => {
    // eslint-disable-next-line no-alert
    if (window.confirm(`Delete estimate ${e.num}?`)) act(() => axios.delete(`${base}/estimates/${e.id}`), 'Estimate deleted');
  };
  if (!rows) return <div className="text-slate-500">Loading…</div>;
  const open = rows.filter((r) => ['pending', 'accepted'].includes(r.status));
  return (
    <div className="space-y-3 text-slate-800" data-testid="estimates-page">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold flex items-center gap-2"><FileText className="h-5 w-5 text-emerald-600" />Estimates</h2>
        <Button className="bg-emerald-600 text-white" onClick={() => go('form', 'estimate')} data-testid="estimates-new"><Plus className="h-4 w-4 mr-1" />Create estimate</Button>
      </div>
      <div className="flex items-center gap-3 text-sm">
        <select className="h-9 rounded border border-slate-300 bg-white px-2" value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="estimates-filter">
          <option value="open">Open</option><option value="closed">Closed</option><option value="rejected">Rejected</option><option value="all">All</option>
        </select>
        {filter === 'open' && <span className="text-slate-500">{open.length} open · {money(open.reduce((s, r) => s + r.remaining, 0))} not yet invoiced</span>}
      </div>
      {msg && <p className="text-sm text-emerald-700">{msg}</p>}{err && <p className="text-sm text-rose-600">{err}</p>}
      <div className={`rounded-lg border overflow-auto max-h-[60vh] ${CARD}`}>
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-white"><tr className="text-left text-slate-500 border-b border-slate-200">
            <th className="p-2">Date</th><th className="p-2">No.</th><th className="p-2">Customer</th><th className="p-2">Expires</th><th className="p-2">Status</th>
            <th className="p-2 text-right">Total</th><th className="p-2 text-right">Invoiced</th><th className="p-2" /></tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={8} className="p-3 text-slate-500">No estimates.</td></tr>}
            {rows.map((e) => (
              <tr key={e.id} className="border-b border-slate-100" data-testid={`estimate-row-${e.id}`}>
                <td className="p-2 whitespace-nowrap">{fmtDate(e.date)}</td><td className="p-2">{e.num}</td><td className="p-2">{e.customer}</td>
                <td className={`p-2 whitespace-nowrap ${e.expiration_date && e.expiration_date < today() && e.status === 'pending' ? 'text-rose-600' : ''}`}>{e.expiration_date ? fmtDate(e.expiration_date) : ''}</td>
                <td className="p-2">{STATUS[e.status]}</td>
                <td className="p-2 text-right tabular-nums">{money(e.total)}</td><td className="p-2 text-right tabular-nums">{money(e.invoiced_total)}</td>
                <td className="p-2 whitespace-nowrap text-right space-x-2">
                  {['pending', 'accepted'].includes(e.status) && <>
                    <button className="text-emerald-600 hover:underline" onClick={() => invoice(e, 'all')} data-testid={`est-invoice-${e.id}`}>{e.invoiced_total ? 'Invoice the rest' : 'Create invoice'}</button>
                    <button className="text-emerald-600 hover:underline" onClick={() => invoice(e, 'percent')} data-testid={`est-progress-${e.id}`}>Progress invoice</button>
                    {e.status === 'pending' && <button className="text-slate-600 hover:underline" onClick={() => status(e, 'accepted')}>Accept</button>}
                    {!e.invoiced_total && <button className="text-slate-600 hover:underline" onClick={() => status(e, 'rejected')}>Reject</button>}
                    {e.invoiced_total > 0 && <button className="text-slate-600 hover:underline" onClick={() => status(e, 'closed')}>Close</button>}
                  </>}
                  <button className="text-slate-600 hover:underline" onClick={() => pdf(e)}>PDF</button>
                  {!e.invoiced_total && <button className="text-rose-600 hover:underline" onClick={() => del(e)}>Delete</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
